'use client';

import * as React from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

interface ComboboxContextType {
  open: boolean;
  setOpen: (open: boolean) => void;
  query: string;
  setQuery: (query: string) => void;
  value?: string;
  onValueChange?: (value: string) => void;
  filteredItems: readonly string[];
  activeIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  selectItem: (value: string) => void;
}

const ComboboxContext = React.createContext<ComboboxContextType | undefined>(
  undefined
);

const useCombobox = () => {
  const context = React.useContext(ComboboxContext);
  if (!context) {
    throw new Error('useCombobox must be used within a Combobox');
  }
  return context;
};

interface ComboboxProps {
  items: readonly string[];
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  children?: React.ReactNode;
}

const Combobox = React.forwardRef<HTMLDivElement, ComboboxProps>(
  ({ items, value, onValueChange, className, children }, ref) => {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState(value ?? '');
    const [activeIndex, setActiveIndex] = React.useState(0);
    const wrapperRef = React.useRef<HTMLDivElement | null>(null);

    const filteredItems = React.useMemo(() => {
      const normalizedQuery = query.toLowerCase().trim();

      if (!normalizedQuery) {
        return items;
      }

      const valueNormalized = String(value ?? '')
        .toLowerCase()
        .trim();
      if (open && valueNormalized && normalizedQuery === valueNormalized) {
        return items;
      }

      const exactMatches = items.filter(
        item => item.toLowerCase() === normalizedQuery
      );
      const prefixMatches = items.filter(
        item =>
          item.toLowerCase().startsWith(normalizedQuery) &&
          item.toLowerCase() !== normalizedQuery
      );
      const containsMatches = items.filter(
        item =>
          item.toLowerCase().includes(normalizedQuery) &&
          !item.toLowerCase().startsWith(normalizedQuery)
      );

      return [...exactMatches, ...prefixMatches, ...containsMatches];
    }, [items, query, open, value]);

    React.useEffect(() => {
      setQuery(value ?? '');
    }, [value]);

    React.useEffect(() => {
      if (!open) {
        setActiveIndex(0);
      }
    }, [open]);

    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(event.target as Node)
        ) {
          setOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectItem = (newValue: string) => {
      onValueChange?.(newValue);
      setQuery(newValue);
      setOpen(false);
    };

    return (
      <ComboboxContext.Provider
        value={{
          open,
          setOpen,
          query,
          setQuery,
          value,
          onValueChange,
          filteredItems,
          activeIndex,
          setActiveIndex,
          selectItem,
        }}
      >
        <div
          ref={node => {
            wrapperRef.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref)
              (ref as React.MutableRefObject<HTMLDivElement | null>).current =
                node;
          }}
          className={cn('relative w-full', className)}
        >
          {children}
        </div>
      </ComboboxContext.Provider>
    );
  }
);
Combobox.displayName = 'Combobox';

const ComboboxInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, onChange, onFocus, ...props }, ref) => {
  const {
    open,
    setOpen,
    query,
    setQuery,
    setActiveIndex,
    activeIndex,
    filteredItems,
    selectItem,
  } = useCombobox();

  const isDisabled = !!props.disabled;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      setOpen(true);
      setActiveIndex(current =>
        current < filteredItems.length - 1 ? current + 1 : current
      );
      event.preventDefault();
    }

    if (event.key === 'ArrowUp') {
      setOpen(true);
      setActiveIndex(current => (current > 0 ? current - 1 : 0));
      event.preventDefault();
    }

    if (event.key === 'Enter' && open && filteredItems[activeIndex]) {
      selectItem(filteredItems[activeIndex]);
      event.preventDefault();
    }

    if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <input
        ref={ref}
        value={query}
        onChange={event => {
          if (isDisabled) return;
          setQuery(event.target.value);
          setOpen(true);
          setActiveIndex(0);
          onChange?.(event);
        }}
        onFocus={event => {
          if (isDisabled) return;
          setOpen(true);
          onFocus?.(event);
        }}
        onKeyDown={event => {
          if (isDisabled) return;
          handleKeyDown(event);
        }}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground',
          isDisabled && 'cursor-not-allowed opacity-60 bg-muted',
          className
        )}
        style={{ caretColor: 'currentColor' }}
        {...props}
      />
      <ChevronDown
        className={cn(
          'absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground',
          isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        )}
        onClick={() => {
          if (!isDisabled) setOpen(!open);
        }}
      />
    </div>
  );
});
ComboboxInput.displayName = 'ComboboxInput';

const ComboboxContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { open } = useCombobox();

  if (!open) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={cn(
        'absolute top-full left-0 right-0 z-50 mt-1 border border-input rounded-md bg-background shadow-md',
        className
      )}
      {...props}
    />
  );
});
ComboboxContent.displayName = 'ComboboxContent';

const ComboboxEmpty = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { filteredItems } = useCombobox();

  if (filteredItems.length > 0) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={cn(
        'py-6 text-center text-sm text-muted-foreground',
        className
      )}
      {...props}
    />
  );
});
ComboboxEmpty.displayName = 'ComboboxEmpty';

interface ComboboxListProps extends React.HTMLAttributes<HTMLDivElement> {
  renderItem?: (item: string) => React.ReactNode;
}

const ComboboxList = React.forwardRef<HTMLDivElement, ComboboxListProps>(
  ({ renderItem, children, className, ...props }, ref) => {
    const { filteredItems } = useCombobox();

    return (
      <div
        ref={ref}
        className={cn('max-h-[300px] overflow-y-auto', className)}
        {...props}
      >
        {renderItem ? filteredItems.map(item => renderItem(item)) : children}
      </div>
    );
  }
);
ComboboxList.displayName = 'ComboboxList';

interface ComboboxItemProps {
  value: string;
  children?: React.ReactNode;
}

const ComboboxItem = React.forwardRef<
  HTMLDivElement,
  ComboboxItemProps & React.HTMLAttributes<HTMLDivElement>
>(({ value, children, className, onClick, ...props }, ref) => {
  const {
    activeIndex,
    setActiveIndex,
    selectItem,
    filteredItems,
    value: selectedValue,
  } = useCombobox();
  const index = filteredItems.indexOf(value);
  const isActive = index === activeIndex;

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
        isActive
          ? 'bg-accent text-accent-foreground'
          : 'hover:bg-accent hover:text-accent-foreground',
        className
      )}
      onMouseEnter={() => setActiveIndex(index)}
      onClick={event => {
        selectItem(value);
        onClick?.(event);
      }}
      {...props}
    >
      <div className="absolute right-2 top-1/2 -translate-y-1/2">
        <Check
          className={cn(
            'h-4 w-4',
            value === selectedValue ? 'opacity-100' : 'opacity-0'
          )}
        />
      </div>
      <div className="flex-1 pr-8">{children}</div>
    </div>
  );
});
ComboboxItem.displayName = 'ComboboxItem';

export {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxList,
  ComboboxItem,
};
